/**
 * <TutorLivePage> — le live du masteur : la pellicule, le centre, la session avec le champion (page).
 *
 * La pellicule du round 2 (Wajih, 02/11), retravaillée au round 3 pour que **le contenu au centre ait la
 * place principale** :
 *  - plus de bandeau en tête (round 4) : le masteur, le direct et sa durée, la cloche descendent dans la
 *    barre du bas ; « Tous en Concentré » et, en séance d'essai, « Prendre RDV » sont dans « Plus » ;
 *  - à gauche, la pellicule (`<TutorFilmstrip>`) : la vidéo de chaque élève et son mode, la mienne en
 *    bas ; chaque élève est une carte (sa vidéo, son mode, l'activité qu'il a devant lui), l'élève regardé
 *    avec sa liste ;
 *  - au centre (`<TutorLiveCenter>`) : la ligne d'objectif (l'élève, ses matières, son prochain contrôle en
 *    puce), les onglets et les gestes, puis le contenu ;
 *  - à droite, la session de l'élève regardé avec le champion (`<TutorSessionFeed>`, piste C choisie par Wajih le
 *    02/11), ancrée, **à largeur fixe** (Wajih, 04/11 : ni l'agrandir ni la rétrécir) ; ce qui se relit monte au
 *    centre : la relecture des propositions (`feed.review`, `<TutorReviewStage>`) et l'aperçu d'une proposition
 *    survolée (`feed.peek`) se posent sur le centre, la pellicule restant visible ; une image tenue au-dessus de la
 *    salle (`feed.drop`) fait de toute la salle la zone de dépôt ;
 *  - en bas, la barre (`<TutorLiveControlBar>`), où revient la mosaïque ;
 *  - par-dessus, le chat en volet (`<LiveChat viewer="tutor">`) et tout le reste dans `<TutorLiveOverlays>`.
 *
 * **La mosaïque** (`bar.mosaic`) est un mode de tout l'écran (`<TutorLiveMosaic>`) : tout le
 * groupe à la place de la pellicule et du centre ; la colonne de droite reste (le groupe), une main levée ne
 * doit pas disparaître.
 *
 * **Les chevrons** (`panes`) replient la pellicule et la session pour élargir le centre (40 et 80 px, les
 * largeurs de l'ancien focus) ; repliées, les colonnes gardent leurs visages (`<TutorLiveRail>`).
 * L'ancien focus les repliait d'office au début d'une présentation sur une tablette : l'écran le
 * décide, la page le reçoit.
 *
 * « Son profil » (le visage de la ligne du centre) ouvre la fiche de l'élève (`profile`,
 * `<TutorLiveStudentPanel>`) là où est la session : à la place de sa colonne quand elle est ancrée, dans
 * son tiroir sinon, tout l'écran au téléphone. Son retour compte ce qui attend une décision ; au téléphone, il
 * rend ses activités (« Ses activités »).
 *
 * Ses props suivent ses zones : `students`, `center`, `feed`, `profile`, `bar`, `overlays`.
 *
 * **Le repli est conçu, pas subi** — c'est la largeur de la page qui tranche, mesurée une fois ici :
 *  - à partir de 1 200 px, la pellicule large (232 px) et le fil (320) ;
 *  - de 1 024 à 1 200, la même pellicule resserrée (208) et le fil (296) ;
 *  - de 600 à 1 024 (la tablette), le fil passe en tiroir (la cloche de la barre et son compteur) ;
 *  - sous 600 (le téléphone), la pellicule devient une rangée de bulles (`<TutorStudentBubbles>`) et la
 *    liste de l'élève regardé l'onglet Activités du centre. Partager et « Présenter » y restent : l'app
 *    les offre au téléphone aujourd'hui.
 * Ancré, le fil ne fait monter aucune snackbar. La lueur n'est jamais dans le live.
 *
 * **Le centre seul** (`centerOnly`) : quand « Présenter » partage l'onglet entier (un navigateur qui ne
 * sait pas recadrer, Safari ou Firefox) ou l'écran entier (l'app), la page ne garde que le centre et la
 * barre (sans sa cloche) — ni pellicule, ni bulles, ni fil, ni fiche, ni chat, ni mosaïque : aucun prénom
 * ni aucune vidéo d'élève ne part chez les élèves (la tête du centre tait l'élève, `hideIdentity`). La barre de dessin et « Arrêter » sont dans la tête
 * du centre, ils restent ; les boîtes (`overlays`) aussi, ouvertes par le masteur.
 *
 * À la place de la salle (`notice`) : la connexion qui s'établit ou se rétablit (« Réessayer »,
 * « Annuler »), aucun élève encore là, aucun élève inscrit. Un avis passe devant la mosaïque : la page montre la
 * salle tant qu'il dure, le mode choisi revient après (`tutorLivePageModes`).
 *
 * **La zone sûre** est la sienne : la page couvre l'écran (la route du live n'a ni en-tête ni coquille),
 * alors elle se garde de l'encoche et de l'heure en haut, de la barre d'accueil en bas (iOS). La bande du
 * haut prend le fond de la page (plus de bandeau, round 4), celle du bas la couleur de la barre, qui garde
 * sa marge quand il n'y a rien à éviter (le web, Android dans son cadre où `useSafeAreaInsets()` lit zéro).
 * Toute la page en dépend, quel que soit ce qu'elle rend : connexion, centre seul, mosaïque.
 *
 * Contrôlée de bout en bout : l'élève regardé, chaque volet ouvert, chaque dialogue, chaque onglet
 * arrive en prop — chaque état s'ouvre seul dans une story. Le moteur (LiveKit) pose les pistes par
 * `renderVideo`.
 */
import React from "react"
import { Pressable, StyleSheet as RNStyleSheet, type LayoutChangeEvent, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { type UILiveParticipant, type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import {
    type UITutorFeedItem,
    type UITutorSession,
    type UITutorSessionRibbon,
    type UITutorLiveNotice,
    type UITutorLiveRoomKind,
    type UITutorLiveStudent,
} from "../../../ports/primary/ui/types/tutorLive"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { CHAMPIONS, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { TutorLiveControlBar, type TutorLiveControlBarProps } from "../../molecules/TutorLiveControlBar"
import { TutorStudentBubbles } from "../../molecules/TutorStudentBubbles"
import { RAIL_WIDTH, TutorLiveRail } from "../../molecules/TutorLiveRail"
import { tutorPendingLabel, tutorSessionElsewhere } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import { tutorFeedSign } from "../../molecules/TutorSessionFeedCard"
import { LiveChat, type LiveChatProps } from "../../organisms/LiveChat"
import { TutorFilmstrip } from "../../organisms/TutorFilmstrip"
import { TutorLiveMosaic } from "../../organisms/TutorLiveMosaic"
import { TutorLiveStudentPanel, type TutorLiveStudentPanelProps } from "../../organisms/TutorLiveStudentPanel"
import { TutorLiveCenter, type TutorLiveCenterProps } from "../../organisms/TutorLiveCenter"
import { TutorSessionFeed, type TutorSessionComposer } from "../../organisms/TutorSessionFeed"
import { type TutorSessionEntryProps } from "../../organisms/TutorSessionEntry"
import {
    TutorReviewPeek,
    TutorReviewStage,
    type TutorReviewPeekProps,
    type TutorReviewStageProps,
} from "../../organisms/TutorReviewStage"
import { TutorSessionRibbon } from "../../molecules/TutorSessionRibbon"
import { Avatar } from "../../atoms/Avatar"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { TutorLiveOverlays, type TutorLiveOverlaysProps } from "../../templates/TutorLiveOverlays"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { shadowStyle } from "../../utils/shadow"
import { tutorLivePageModes } from "./tutorLivePageModes"

/** Les élèves de la salle : la pellicule (ou les bulles), l'élève regardé, sa liste. */
export type TutorLiveStudentsZone = {
    list: UITutorLiveStudent[]
    /**
     * L'élève regardé : sa liste sous sa vidéo (au téléphone, l'onglet Activités), sa tête et sa bascule au
     * centre. `center.focus` est une activité de cet élève : l'écran la change avec lui.
     */
    selectedId?: string
    onSelect: (studentId: string) => void
    onChangeMode: (studentId: string, mode: UISoundMode) => void
    modePendingId?: string
    onOpenActivity: (studentId: string, activityId: string) => void
    onAddActivity: (studentId: string) => void
    onOpenProfile: (studentId: string) => void
    onReorder?: (studentId: string, activityIds: string[]) => void
    onSeeMoreDone?: (studentId: string) => void
    /** Les non-lus d'un élève, sur sa vidéo : son fil du chat. */
    onOpenChat?: (studentId: string) => void
    /** Son prochain contrôle : « Contrôles » au centre. */
    onOpenAssessments?: (studentId: string) => void
    /** Ses échanges avec le champion, « … · n ». */
    onOpenHelper?: (studentId: string) => void
}

/** Le centre : ce que `<TutorLiveCenter>` reçoit, hors des élèves et des pistes que la page lui donne. */
export type TutorLiveCenterZone = Omit<
    TutorLiveCenterProps,
    "students" | "selectedId" | "renderVideo" | "phone" | "gestures" | "testID"
>

/**
 * La colonne de droite : la session de l'élève regardé avec le champion, et les signaux de la séance (la
 * cloche, la colonne repliée, les urgences de « Ailleurs »). Son tiroir sous 1 024 px.
 */
export type TutorLiveFeedZone = {
    /** Les signaux de la séance, de tous les élèves (l'ancien panneau d'événements). */
    items: UITutorFeedItem[]
    onAction: (itemId: string, actionId: string) => void
    onDismiss: (itemId: string) => void
    open?: boolean
    onToggle: () => void
    /**
     * Le fil que la colonne montre (`tutorSessionTimeline`) : filtré sur l'élève regardé (`student`), ou celui de
     * tout le groupe — le filtre retiré, et toujours en mosaïque. L'écran, qui tient le filtre, le choisit.
     */
    session?: UITutorSession | null
    /** Retirer ou remettre le filtre sur l'élève regardé (la puce du titre et son ×, « Seulement Léa »). */
    onToggleFilter?: () => void
    onResult?: TutorSessionEntryProps["onResult"]
    /** Le champ : sa cible suit le fil, l'élève filtré ou tout le groupe. */
    composer?: TutorSessionComposer
    /** La carte dont un geste est en vol (sa roue). Absente (défaut) : aucune roue. */
    pending?: { itemId: string; actionId: string }
    /** Un geste d'une ligne de la génération : « Revoir », envoyer, « <champion> propose ». */
    onGeneration?: TutorSessionEntryProps["onGeneration"]
    /** Une proposition survolée dans la carte « prêtes » (web). */
    onPeek?: TutorSessionEntryProps["onPeek"]
    /** La relecture des propositions, au centre. Absente ou fermée : le centre. */
    review?: TutorReviewStageProps | null
    /** L'aperçu d'une proposition survolée, au centre. */
    peek?: TutorReviewPeekProps | null
    /** Le ruban de la séance de l'élève regardé, sous l'en-tête de la colonne. */
    ribbon?: UITutorSessionRibbon | null
    /** Une image tenue au-dessus de la salle (web) : pour qui elle générera (`null` : tout le groupe). */
    drop?: { firstname: string | null; avatarUrl?: string } | null
}

/** La barre du bas, hors de la fin qui tourne (dans `overlays.end`). */
export type TutorLiveBarZone = Omit<TutorLiveControlBarProps, "ending" | "presence" | "bell" | "voiceId" | "testID">

/** Ce qui se pose par-dessus : le chat en volet, et tout ce que porte `<TutorLiveOverlays>`. */
export type TutorLiveOverlaysZone = Omit<TutorLiveOverlaysProps, "more"> & {
    /** Le chat ouvert : les props de `<LiveChat>` (sans `viewer`, toujours le masteur). */
    chat?: Omit<LiveChatProps, "viewer"> | null
    /**
     * « Plus », dont les gestes de l'ancien bandeau (round 4) : « Tous en Concentré » (sauf un individuel) et,
     * en séance d'essai, « Prendre RDV ». Ce que la page sait seule (`allFocused`) n'y est pas.
     */
    more: Omit<TutorLiveOverlaysProps["more"], "allFocused">
}

/** La fiche de l'élève ouverte par « Son profil » : ce que la page ne décide pas d'elle-même. */
export type TutorLiveProfileZone = Pick<TutorLiveStudentPanelProps, "fiche" | "onBack" | "onClose">

/** Les colonnes repliées par leurs chevrons. */
export type TutorLivePanesZone = {
    filmstripCollapsed: boolean
    feedCollapsed: boolean
    onToggleFilmstrip: () => void
    onToggleFeed: () => void
}

export type TutorLivePageProps = {
    me: UILiveParticipant
    roomKind?: UITutorLiveRoomKind
    /** L'instant où le live a commencé (ms) : le chrono de la barre. */
    startedAt: number
    /** À la place de la salle. */
    notice?: UITutorLiveNotice
    onRetryConnection?: () => void
    onCancelConnection?: () => void
    students: TutorLiveStudentsZone
    center: TutorLiveCenterZone
    feed: TutorLiveFeedZone
    /** La fiche ouverte. Absente : le fil. */
    profile?: TutorLiveProfileZone | null
    bar: TutorLiveBarZone
    overlays: TutorLiveOverlaysZone
    /** Les chevrons de la pellicule et du fil. Absent : pas de chevron. */
    panes?: TutorLivePanesZone
    /** Le champion qui aide en séance (le monde de jeu de l'espace) : il signe la session et la pellicule. */
    helper: ChampionName
    /** La vraie piste d'un participant (`me.id` pour la mienne). */
    renderVideo?: (participantId: string) => React.ReactNode
    /**
     * Le centre seul, tant que « Présenter » partage tout l'onglet ou tout l'écran (pas de recadrage).
     * Défaut `false` : la salle entière.
     */
    centerOnly?: boolean
    testID?: string
}

const TEXTS = {
    connecting: "Connexion…",
    reconnecting: "Reconnexion…",
    mediaPendingTitle: "Autorise ta caméra et ton micro",
    // Sans dire où : la question du navigateur n'est pas au même endroit partout (une bulle près de l'adresse, une
    // fenêtre au milieu, le téléphone).
    mediaPending: frenchSpacing(
        "Ton navigateur te demande l'accès à ta caméra et à ton micro. Choisis « Autoriser » dans sa question pour entrer dans ton live."
    ),
    retry: "Réessayer",
    cancel: "Annuler",
    noStudentYet:
        "Aucun élève connecté pour le moment. Les parents sont prévenus automatiquement au bout de 5 minutes. Si tu n'as aucune nouvelle au bout de 20 minutes, tu peux clôturer le live.",
    noStudentEnrolled: "Aucun élève inscrit sur cette séance.",
    closeFeed: "Fermer la session",
    expandStudents: "Déplier les élèves",
    expandFeed: "Déplier la session",
    closeProfile: "Fermer sa fiche",
    backToFeed: "La session",
    waiting: (firstname: string, urgent: string | undefined, pending: number) =>
        [firstname, urgent, pending > 0 ? tutorPendingLabel(pending) : null].filter(Boolean).join(", "),
    backToActivities: "Ses activités",
    look: (firstname: string) => `Regarder ${firstname}`,
    dropTitle: "Lâche l'image pour générer",
    dropFor: (firstname: string | null) =>
        firstname != null ? `Pour ${firstname}, l'élève que tu regardes` : "Pour tout le groupe",
}

/** Les seuils de la salle (la largeur de la page). */
const WIDE_FROM = 1200
const FEED_ANCHORED_FROM = 1024
const PHONE_BELOW = 600
const SIDE_PANEL_MAX = 380
/** La fiche de l'élève est plus large que le fil : ses cartes et ses onglets ont besoin de la place. */
const PROFILE_PANEL_WIDTH = 420
/** La pellicule et le fil, large puis resserré. */
const FILMSTRIP_WIDTH = { wide: 232, narrow: 208 }
/**
 * La colonne du champion, à largeur fixe (Wajih, 04/11 : « ni l'agrandir ni la rétrécir ») : 380 au large, comme
 * les maquettes ; 320 de 1 024 à 1 200, où le centre n'aurait plus la place de l'activité.
 */
const FEED_WIDTH = { wide: 380, narrow: 320 }

export function TutorLivePage(props: TutorLivePageProps) {
    const {
        me,
        roomKind = "shared",
        startedAt,
        notice,
        onRetryConnection,
        onCancelConnection,
        students,
        center,
        feed,
        profile,
        bar,
        overlays,
        panes,
        helper,
        renderVideo,
        centerOnly = false,
        testID,
    } = props
    const insets = useSafeAreaInsets()
    const wideWidth = useWidthAtLeast(WIDE_FROM)
    const feedWidth = useWidthAtLeast(FEED_ANCHORED_FROM)
    const tabletWidth = useWidthAtLeast(PHONE_BELOW)
    const onLayout = (event: LayoutChangeEvent) => {
        wideWidth.onLayout(event)
        feedWidth.onLayout(event)
        tabletWidth.onLayout(event)
    }
    const size = wideWidth.atLeast ? "wide" : "narrow"
    const anchored = feedWidth.atLeast
    const phone = !tabletWidth.atLeast
    const around = !centerOnly
    const filmstripCollapsed = panes?.filmstripCollapsed === true
    const feedCollapsed = anchored && panes?.feedCollapsed === true
    const individual = roomKind === "individual"
    const { chat, more, ...surfaces } = overlays
    const presenting = center.present?.on === true

    const session = feed.session
    // Le centre seul n'a pas de mosaïque ; un avis qui tient la salle (la connexion qui s'établit ou se rétablit, la
    // question de la caméra et du micro) non plus : son message et ses gestes sont à la place de la salle, que la
    // mosaïque recouvrirait. Un bandeau « Aucun élève » ne tient rien (`tutorLivePageModes`).
    const { held, mosaic } = tutorLivePageModes({ centerOnly, mosaicChosen: bar.mosaic?.on === true, notice })

    const focusStudentId = mosaic ? undefined : students.selectedId
    // Un seul fil : filtré sur l'élève regardé quand il le dit (`student`), sinon celui de tout le groupe ;
    // la mosaïque n'est jamais filtrée, quoi que l'écran passe.
    const filtered = !mosaic && session?.student != null
    const focusStudent = students.list.find((student) => student.id === focusStudentId)
    // Ce qui attend le masteur, de tous les élèves : la pastille de la cloche (l'ancien `countActiveEvents`).
    const waiting = students.list.reduce((count, student) => count + (student.pending ?? 0), 0)
    // Les élèves qui attendent, une fois : « Ailleurs » (sans l'élève regardé) et la colonne repliée (tous).
    const waitingRows = tutorSessionElsewhere({ students: students.list, items: feed.items })
    // Au centre seul, pas de cloche : elle ouvrirait une session que la page ne montre pas.
    const bell =
        anchored || !around
            ? undefined
            : {
                  count: waiting,
                  urgent: feed.items.some((item) => item.priority === "urgent"),
                  open: feed.open === true,
                  onPress: feed.onToggle,
              }

    const feedColumn = (
        <TutorSessionFeed
            helper={helper}
            session={session}
            filter={
                focusStudent != null && feed.onToggleFilter != null
                    ? {
                          // Le prénom de la puce est celui du fil montré, pas de l'élève en cours de bascule.
                          firstname: (filtered ? session?.student?.firstname : undefined) ?? focusStudent.firstname,
                          onToggle: feed.onToggleFilter,
                      }
                    : undefined
            }
            elsewhere={filtered ? waitingRows.filter((row) => row.student.id !== focusStudentId) : []}
            onSelectStudent={students.onSelect}
            onAction={feed.onAction}
            pending={feed.pending}
            onDismiss={feed.onDismiss}
            onResult={feed.onResult}
            onGeneration={feed.onGeneration}
            onPeek={feed.onPeek}
            peekId={feed.peek != null ? String(feed.peek.proposal.id) : null}
            composer={session == null ? undefined : feed.composer}
            ribbon={
                // Le ruban est celui de l'élève regardé : le fil filtré sur lui, jamais la mosaïque. Il s'efface le temps
                // qu'un réglage est déplié dans le champ : sa place revient au bouton d'envoi (relevé en recette).
                filtered && feed.ribbon != null && feed.ribbon.now > 0 && feed.composer?.editor == null ? (
                    <TutorSessionRibbon {...feed.ribbon} />
                ) : null
            }
            onClose={anchored ? undefined : feed.onToggle}
            onCollapse={anchored ? panes?.onToggleFeed : undefined}
        />
    )

    // Ce qui se pose sur le centre (ou la mosaïque) : la relecture, sinon l'aperçu d'une proposition survolée. Rien en
    // centre seul : tout l'écran part chez les élèves, et la relecture n'est pas à leur montrer.
    const centerOverlay = !around ? null : feed.review?.open === true ? (
        <TutorReviewStage {...feed.review} />
    ) : feed.peek != null && !presenting ? (
        <TutorReviewPeek {...feed.peek} />
    ) : null

    const profilePanel =
        around && profile != null ? (
            <TutorLiveStudentPanel
                {...profile}
                backLabel={phone ? TEXTS.backToActivities : TEXTS.backToFeed}
                backCount={phone ? 0 : waiting}
            />
        ) : null

    // « Aucun élève… » se pose au-dessus de la salle comme de la mosaïque : il ne tient rien (`tutorLivePageModes`).
    const banner =
        notice === "no_student_yet" ? (
            <Banner message={TEXTS.noStudentYet} icon="clock" color="warning" tinted />
        ) : notice === "no_student_enrolled" ? (
            <Banner message={TEXTS.noStudentEnrolled} icon="user-friends" color="danger" tinted />
        ) : null

    const room = held ? (
        <View style={styles.connecting}>
            <Stack gap="4" align="center" style={styles.connectingText}>
                {notice === "media_pending" ? (
                    <Stack gap="2" align="center">
                        <Icon name="video" size={24} color="role" />
                        <Text size="text-lg" weight="bold" center accessibilityRole="header">
                            {TEXTS.mediaPendingTitle}
                        </Text>
                        <Text color="text-muted" center>
                            {TEXTS.mediaPending}
                        </Text>
                    </Stack>
                ) : (
                    <WaitingText>{notice === "reconnecting" ? TEXTS.reconnecting : TEXTS.connecting}</WaitingText>
                )}
                <Row gap="2">
                    {onCancelConnection != null && (
                        <Button label={TEXTS.cancel} variant="ghost" onPress={onCancelConnection} />
                    )}
                    {onRetryConnection != null && (
                        <Button label={TEXTS.retry} variant="outline" onPress={onRetryConnection} />
                    )}
                </Row>
            </Stack>
        </View>
    ) : (
        <Stack gap="3" style={styles.flex}>
            {banner}
            <TutorLiveCenter
                {...center}
                students={students.list}
                selectedId={students.selectedId}
                renderVideo={renderVideo}
                phone={phone}
                gestures={students}
            />
        </Stack>
    )

    return (
        <View style={styles.root(insets)} onLayout={onLayout} testID={testID}>
            {around && phone && !held && !mosaic && students.list.length > 0 && (
                <View style={styles.bubbles}>
                    <TutorStudentBubbles
                        students={students.list}
                        selectedId={students.selectedId}
                        onSelect={students.onSelect}
                        me={me}
                        renderVideo={renderVideo}
                    />
                </View>
            )}
            <View style={styles.body}>
                {mosaic && (
                    <Stack gap="3" style={[styles.center(phone), styles.centerStage]}>
                        {banner}
                        {centerOverlay}
                        <TutorLiveMosaic
                            students={students.list}
                            me={me}
                            onSelect={students.onSelect}
                            onOpenActivity={students.onOpenActivity}
                            onChangeMode={students.onChangeMode}
                            modePendingId={students.modePendingId}
                            renderVideo={renderVideo}
                        />
                    </Stack>
                )}
                {around && !mosaic && !phone && !held && students.list.length > 0 && filmstripCollapsed && (
                    <View style={styles.rail(RAIL_WIDTH.left, "left")}>
                        <TutorLiveRail
                            side="left"
                            items={students.list.map((student) => ({
                                id: student.id,
                                firstname: student.firstname,
                                avatarUrl: student.avatarUrl,
                                selected: student.id === students.selectedId,
                                label: TEXTS.look(student.firstname),
                            }))}
                            onPressItem={students.onSelect}
                            onExpand={panes!.onToggleFilmstrip}
                            expandLabel={TEXTS.expandStudents}
                        />
                    </View>
                )}
                {around && !mosaic && !phone && !held && students.list.length > 0 && !filmstripCollapsed && (
                    <View style={styles.filmstrip(FILMSTRIP_WIDTH[size])}>
                        <TutorFilmstrip
                            students={students.list}
                            selectedId={students.selectedId}
                            onSelect={students.onSelect}
                            onChangeMode={students.onChangeMode}
                            modePendingId={students.modePendingId}
                            openActivityId={center.focus?.activityId}
                            onOpenActivity={students.onOpenActivity}
                            onReorder={students.onReorder}
                            onSeeMoreDone={students.onSeeMoreDone}
                            onOpenChat={students.onOpenChat}
                            helperName={CHAMPIONS[helper].label}
                            onOpenHelper={students.onOpenHelper}
                            onCollapse={panes?.onToggleFilmstrip}
                            me={me}
                            meState={presenting ? "presenting" : bar.screenShare?.on === true ? "sharing" : undefined}
                            renderVideo={renderVideo}
                        />
                    </View>
                )}
                {!mosaic && (
                    <View style={[styles.center(phone), styles.centerStage]}>
                        {room}
                        {!held && centerOverlay}
                    </View>
                )}
                {anchored && profilePanel != null && <View style={styles.profile}>{profilePanel}</View>}
                {around && anchored && profilePanel == null && feedCollapsed && (
                    <View style={styles.rail(RAIL_WIDTH.right, "right")}>
                        <TutorLiveRail
                            side="right"
                            items={waitingRows.map((row) => ({
                                id: row.student.id,
                                firstname: row.student.firstname,
                                avatarUrl: row.student.avatarUrl,
                                sign: row.urgent != null ? tutorFeedSign(row.urgent) : undefined,
                                label: TEXTS.waiting(row.student.firstname, row.urgent?.text, row.pending),
                            }))}
                            onPressItem={panes!.onToggleFeed}
                            onExpand={panes!.onToggleFeed}
                            expandLabel={TEXTS.expandFeed}
                        />
                    </View>
                )}
                {around && anchored && profilePanel == null && !feedCollapsed && (
                    <View style={styles.feed(FEED_WIDTH[size])}>{feedColumn}</View>
                )}
                {!anchored && profilePanel != null && (
                    <>
                        <Pressable
                            style={styles.scrim}
                            onPress={profile?.onClose}
                            accessibilityLabel={TEXTS.closeProfile}
                        />
                        <View style={styles.profileDrawer(!phone)}>{profilePanel}</View>
                    </>
                )}

                {around && !anchored && profilePanel == null && feed.open === true && (
                    <>
                        <Pressable style={styles.scrim} onPress={feed.onToggle} accessibilityLabel={TEXTS.closeFeed} />
                        <View style={styles.drawer(phone)}>{feedColumn}</View>
                    </>
                )}
                {around && chat != null && (
                    <View style={[styles.drawer(phone), styles.chat]}>
                        <LiveChat {...chat} viewer="tutor" />
                    </View>
                )}
                {around && feed.drop != null && (
                    <View style={styles.drop} pointerEvents="none">
                        <Stack gap="4" align="center" style={styles.dropCard}>
                            <IconBubble
                                iconName="file-image"
                                size={64}
                                iconSize={28}
                                color="role-fill"
                                iconColor="text-on-role"
                            />
                            <Text size="text-2xl" weight="bold" color="text-strong" center>
                                {TEXTS.dropTitle}
                            </Text>
                            <Row gap="2" align="center" style={styles.dropTarget}>
                                {feed.drop.firstname != null && (
                                    <Avatar uri={feed.drop.avatarUrl} name={feed.drop.firstname} size={28} />
                                )}
                                <Text weight="semibold" color="role-strong">
                                    {TEXTS.dropFor(feed.drop.firstname)}
                                </Text>
                            </Row>
                        </Stack>
                    </View>
                )}
            </View>
            <View style={styles.bar(insets.bottom)}>
                <TutorLiveControlBar
                    {...bar}
                    voiceId={me.id}
                    presence={{ tutor: me, studentCount: students.list.length, startedAt }}
                    bell={bell}
                    ending={overlays.end.finalizing === true || overlays.end.leaving === true}
                />
            </View>

            <TutorLiveOverlays
                {...surfaces}
                more={{
                    ...more,
                    onAllFocused: individual ? undefined : more.onAllFocused,
                    onBookCall: roomKind === "trial" ? more.onBookCall : undefined,
                    allFocused: students.list.every(
                        (student) => student.presence !== "here" || student.soundMode === "focused"
                    ),
                }}
            />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La zone sûre : l'encoche en haut et sur les côtés, sur le fond de la page (plus de bandeau, round 4). */
    root: (insets: { top: number; left: number; right: number }) => ({
        flex: 1,
        minHeight: 0,
        paddingTop: insets.top,
        paddingLeft: insets.left,
        paddingRight: insets.right,
        backgroundColor: theme.colors.surface,
    }),
    body: { flex: 1, minHeight: 0, flexDirection: "row" },
    bubbles: { paddingHorizontal: theme.spacing["3"], paddingTop: theme.spacing["2"] },
    filmstrip: (width: number) => ({
        width,
        minHeight: 0,
        paddingVertical: theme.spacing["3"],
        paddingLeft: theme.spacing["4"],
        paddingRight: theme.spacing["3"],
        borderRightWidth: 1,
        borderRightColor: theme.colors["border-subtle"],
    }),
    center: (phone: boolean) => ({
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        paddingHorizontal: theme.spacing[phone ? "3" : "4"],
        paddingVertical: theme.spacing["3"],
    }),
    /** Une colonne repliée : sa largeur de l'ancien focus, son filet du côté du centre. */
    rail: (width: number, side: "left" | "right") => ({
        width,
        minHeight: 0,
        paddingVertical: theme.spacing["3"],
        ...(side === "left"
            ? { borderRightWidth: 1, borderRightColor: theme.colors["border-subtle"] }
            : { borderLeftWidth: 1, borderLeftColor: theme.colors["border-subtle"] }),
    }),
    feed: (width: number) => ({
        width,
        minHeight: 0,
        padding: theme.spacing["3"],
        borderLeftWidth: 1,
        borderLeftColor: theme.colors["border-subtle"],
    }),
    profile: {
        width: PROFILE_PANEL_WIDTH,
        minHeight: 0,
        borderLeftWidth: 1,
        borderLeftColor: theme.colors["border-subtle"],
        backgroundColor: theme.colors.surface,
    },
    /** Au téléphone, tout l'écran ; au-delà, la largeur du panneau. */
    profileDrawer: (bounded: boolean) => ({
        position: "absolute" as const,
        top: 0,
        right: 0,
        bottom: 0,
        width: "100%" as const,
        maxWidth: bounded ? PROFILE_PANEL_WIDTH : undefined,
        backgroundColor: theme.colors.surface,
        ...shadowStyle(theme, "floating"),
    }),
    flex: { flex: 1, minHeight: 0 },
    connecting: { flex: 1, alignItems: "center" as const, justifyContent: "center" as const },
    connectingText: { maxWidth: 420, paddingHorizontal: theme.spacing["4"] },
    /** Le centre porte ce qui se pose sur lui : la relecture, l'aperçu. */
    centerStage: { position: "relative" as const },
    /** Une image tenue au-dessus de la salle : toute la salle devient la zone de dépôt. */
    drop: {
        ...RNStyleSheet.absoluteFillObject,
        padding: theme.spacing["3"],
        backgroundColor: theme.colors.scrim,
        zIndex: 10,
    },
    dropCard: {
        flex: 1,
        justifyContent: "center" as const,
        borderRadius: theme.radius.xl,
        borderWidth: 3,
        borderStyle: "dashed" as const,
        borderColor: theme.colors.role,
        backgroundColor: theme.colors.surface,
    },
    dropTarget: {
        paddingLeft: theme.spacing["1"],
        paddingRight: theme.spacing["4"],
        paddingVertical: theme.spacing["1"],
        borderRadius: theme.radius.full,
        borderWidth: 2,
        borderColor: theme.colors.role,
    },
    scrim: { ...RNStyleSheet.absoluteFillObject, backgroundColor: theme.colors.scrim },
    /** Le tiroir : la largeur d'une colonne ; au téléphone, tout l'écran, sans bande de voile à gauche. */
    drawer: (phone: boolean) => ({
        position: "absolute" as const,
        top: 0,
        right: 0,
        bottom: 0,
        width: "100%" as const,
        maxWidth: phone ? undefined : SIDE_PANEL_MAX,
        padding: theme.spacing["3"],
        backgroundColor: theme.colors.surface,
        ...shadowStyle(theme, "floating"),
    }),
    chat: { padding: 0, backgroundColor: theme.colors["surface-raised"] },
    /** La barre d'accueil (iOS) passe dans la barre, qui garde au moins sa marge d'ici. */
    bar: (bottomInset: number) => ({
        paddingBottom: Math.max(bottomInset, theme.spacing["2"]),
        backgroundColor: theme.colors["surface-raised"],
        borderTopWidth: 1,
        borderTopColor: theme.colors["border-subtle"],
    }),
}))
